
// Accesibility reference: https://www.w3.org/TR/wai-aria-practices-1.1/#tabpanel
const KEYS = {
  END: 35,
  HOME: 36,
  LEFT: 37,
  RIGHT: 39,
  ENTER: 13,
  SPACE: 32,
};

const activateTabEvent = new CustomEvent('activatetab', { bubbles: true });
const deactivateTabEvent = new CustomEvent('deactivatetab', { bubbles: true });
const expandPanelEvent = new CustomEvent('expandpanel', { bubbles: true });
const collapsePanelEvent = new CustomEvent('collapsepanel', { bubbles: true });

const expandTabPanel = (tab) => {
  const panelId = tab.getAttribute('aria-controls');
  const panel = document.getElementById(panelId);
  if (!panel || panel.dataset.expanded == 'true') {
    return;
  }

  panel.dispatchEvent(expandPanelEvent);
};

const collapseTabPanel = (tab) => {
  const panelId = tab.getAttribute('aria-controls');
  const panel = document.getElementById(panelId);
  if (!panel || panel.dataset.expanded != 'true') {
    return;
  }

  panel.dispatchEvent(collapsePanelEvent);
};

const selectTab = (tab) => {
  tab.setAttribute('aria-selected', 'true');
  tab.setAttribute('data-selected', 'true');
  tab.removeAttribute('tabindex');
  tab.classList.add('selected');
};

const deselectTab = (tab) => {
  tab.removeAttribute('aria-selected');
  tab.removeAttribute('data-selected');
  tab.setAttribute('tabindex', '-1');
  tab.classList.remove('selected');
};

const deactivateTabs = (tabs) => tabs.forEach((tab) => {
  if (tab.dataset.selected !== 'true') {
    return;
  }

  tab.dispatchEvent(deactivateTabEvent);
});

const activateTabHandler = (tabs) => (event) => {
  const tab = event.target.closest('[role="tab"]');
  expandTabPanel(tab);
  selectTab(tab);
  deactivateTabs(Array.from(tabs).filter((otherTab) => otherTab !== tab));
};

const deactivateTabHandler = () => (event) => {
  const tab = event.target.closest('[role="tab"]');
  collapseTabPanel(tab);
  deselectTab(tab);
};

const clickTabHandler = () => (event) => {
  const tab = event.target.closest('[role="tab"]');
  if (tab.dataset.selected === 'true') {
    return;
  }
  tab.dispatchEvent(activateTabEvent);
};

const expandPanelHandler = () => (event) => {
  const panel = event.target.closest('[role="tabpanel"]');
  panel.removeAttribute('hidden');
  panel.setAttribute('aria-expanded', 'true');
  panel.setAttribute('data-expanded', 'true');
  panel.classList.add('expanded');
};

const collapsePanelHandler = () => (event) => {
  const panel = event.target.closest('[role="tabpanel"]');
  panel.setAttribute('hidden', 'hidden');
  panel.removeAttribute('aria-expanded');
  panel.removeAttribute('data-expanded');
  panel.classList.remove('expanded');
};

const keydownTabHandler = (tabs) => (event) => {
  const key = event.keyCode;

  switch(key) {
    case KEYS.HOME:
      event.preventDefault();
      tabs[0].focus();
      break;

    case KEYS.END:
      event.preventDefault();
      tabs[tabs.length - 1].focus();
      break;
  }
};

const keyupTabHandler = (tabs) => (event) => {
  const key = event.keyCode;
  const tab = event.target.closest('[role="tab"]');

  switch(key) {
    case KEYS.LEFT:
      const prevTab = tabs[Array.from(tabs).indexOf(tab) - 1];
      if (prevTab) {
        prevTab.focus();
      } else {
        tabs[tabs.length - 1].focus();
      }
      break;

    case KEYS.RIGHT:
      const nextTab = tabs[Array.from(tabs).indexOf(tab) + 1];
      if (nextTab) {
        nextTab.focus();
      } else {
        tabs[0].focus();
      }
      break;

    case KEYS.ENTER:
    case KEYS.SPACE:
      if (tab.dataset.selected !== 'true') {
        tab.dispatchEvent(activateTabEvent);
      }
      break;
  }
};

export default function(context = document) {
  const tablists = context.querySelectorAll('[role="tablist"]');

  tablists.forEach((tablist) => {
    const tabs = tablist.querySelectorAll('[role="tab"]');

    tabs.forEach((tab) => {
      tab.addEventListener('activatetab', activateTabHandler(tabs));
      tab.addEventListener('deactivatetab', deactivateTabHandler());
      tab.addEventListener('click', clickTabHandler());
      tab.addEventListener('keydown', keydownTabHandler(tabs));
      tab.addEventListener('keyup', keyupTabHandler(tabs));

      const panelId = tab.getAttribute('aria-controls');
      const panel = document.getElementById(panelId);
      if (panel) {
        panel.addEventListener('expandpanel', expandPanelHandler());
        panel.addEventListener('collapsepanel', collapsePanelHandler());
      }
    });
  });
}
